import { useEffect, useState } from 'react';
import { adminApi } from './api';
import './PaymentHistory.css';

const STATUSES = [
  ['all', 'Tous les statuts'],
  ['pending', 'En attente'],
  ['succeeded', 'Réussis'],
  ['failed', 'Refusés'],
  ['cancelled', 'Annulés'],
  ['expired', 'Expirés'],
];
const PROVIDERS = [
  ['all', 'Tous les moyens'],
  ['orange', 'Orange Money'],
  ['moov', 'Moov Money'],
  ['legacy', 'Anciennes simulations'],
];
const STATUS_LABELS = {
  pending: 'En attente',
  succeeded: 'Réussi (fictif)',
  failed: 'Refusé',
  cancelled: 'Annulé',
  expired: 'Expiré',
};

function formatDate(date) {
  if (!date) return '—';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString('fr-FR') + ' FCFA';
}

function candidateName(candidate = {}) {
  return candidate.pseudonym ||
    [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') ||
    `Candidat #${candidate.id ?? '—'}`;
}

function accessLabel(payment) {
  if (payment.status !== 'succeeded') return 'Non accordé';
  if (!payment.subscription?.entitlementCreated) return 'À vérifier';
  return payment.subscription.entitlementActive ? 'Premium actif' : 'Premium créé · inactif';
}

export default function PaymentHistory({ onUnauthorized }) {
  const [filters, setFilters] = useState({
    search: '', provider: 'all', status: 'all', page: 1,
  });
  const [draftSearch, setDraftSearch] = useState('');
  const [refreshId, setRefreshId] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    adminApi.paymentHistory(filters)
      .then((response) => {
        if (mounted) setData(response);
      })
      .catch((failure) => {
        if (!mounted) return;
        setError(failure.message || 'Impossible de récupérer l’historique des paiements.');
        if (failure.status === 401) onUnauthorized?.();
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  // Only actual search filters and explicit refresh trigger a network request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, refreshId]);

  function selectFilter(key, value) {
    setFilters((previous) => ({ ...previous, [key]: value, page: 1 }));
  }

  function submitSearch(event) {
    event.preventDefault();
    selectFilter('search', draftSearch.trim());
  }

  const payments = data?.payments || [];
  const summary = data?.summary;
  const pagination = data?.pagination;
  const page = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 0;

  return (
    <section className="bo-panel bo-payment-history" aria-label="Historique des paiements">
      <div className="bo-panel__header">
        <div>
          <p className="bo-eyebrow">Suivi administratif</p>
          <h3>Historique des paiements</h3>
          <p className="bo-muted">
            Toutes les transactions de test, y compris les paiements réussis, refusés,
            annulés ou expirés. Aucune somme réelle n’est encaissée ici.
          </p>
        </div>
        <button type="button" className="bo-button bo-button--ghost"
          disabled={loading} onClick={() => setRefreshId((value) => value + 1)}>
          Actualiser l’historique
        </button>
      </div>

      <form className="bo-payment-history__filters" onSubmit={submitSearch}
        aria-label="Filtres de l’historique">
        <label>
          Rechercher un candidat ou une référence
          <input className="bo-input" type="search" value={draftSearch}
            maxLength={120} placeholder="Pseudo, email, référence…"
            onChange={(event) => setDraftSearch(event.target.value)} />
        </label>
        <label>
          Moyen de paiement
          <select className="bo-input" value={filters.provider}
            onChange={(event) => selectFilter('provider', event.target.value)}>
            {PROVIDERS.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          Statut
          <select className="bo-input" value={filters.status}
            onChange={(event) => selectFilter('status', event.target.value)}>
            {STATUSES.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <button type="submit" className="bo-button bo-button--primary">Rechercher</button>
        {(filters.search || filters.provider !== 'all' || filters.status !== 'all') && (
          <button type="button" className="bo-button bo-button--ghost"
            onClick={() => {
              setDraftSearch('');
              setFilters({ search: '', provider: 'all', status: 'all', page: 1 });
            }}>Réinitialiser</button>
        )}
      </form>

      {error && <p className="bo-alert bo-alert--error" role="alert">{error}</p>}
      {loading && <p role="status" className="bo-muted">Chargement de l’historique…</p>}

      {!loading && !error && summary && (
        <>
          <div className="bo-payment-history__summary" aria-label="Synthèse des transactions fictives">
            <div><span>Transactions</span><strong>{summary.total}</strong></div>
            <div><span>Réussies</span><strong>{summary.succeeded}</strong></div>
            <div><span>En attente</span><strong>{summary.pending}</strong></div>
            <div><span>Montant fictif confirmé</span><strong>{formatAmount(summary.succeededAmountXof)}</strong></div>
          </div>

          {payments.length === 0 ? (
            <p className="bo-muted">Aucun paiement ne correspond à ces critères.</p>
          ) : (
            <div className="bo-table-wrap bo-payment-history__table-wrap">
              <table className="bo-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Candidat</th>
                    <th>Référence</th>
                    <th>Portefeuille</th>
                    <th>Formule</th>
                    <th>Montant fictif</th>
                    <th>Statut</th>
                    <th>Accès</th>
                    <th>Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <strong>{candidateName(item.candidate)}</strong>
                        <small className="bo-payment-history__muted">#{item.candidate?.id}</small>
                      </td>
                      <td><code>{item.reference || item.id.slice(0, 12)}</code></td>
                      <td>{item.provider === 'orange' ? 'Orange Money'
                        : item.provider === 'moov' ? 'Moov Money' : 'Simulation antérieure'}</td>
                      <td>{item.durationLabel || item.planCode}</td>
                      <td>{formatAmount(item.amountXof)}</td>
                      <td>
                        <span className={`bo-payment-history__status is-${item.status}`}>
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                      </td>
                      <td>{accessLabel(item)}</td>
                      <td>
                        <details className="bo-payment-history__details">
                          <summary>Consulter</summary>
                          <dl>
                            <div><dt>Candidat</dt><dd>{candidateName(item.candidate)}</dd></div>
                            <div><dt>Email</dt><dd>{item.candidate?.email || '—'}</dd></div>
                            <div><dt>Identifiant de transaction</dt><dd><code>{item.id}</code></dd></div>
                            <div><dt>Référence</dt><dd>{item.reference || '—'}</dd></div>
                            <div><dt>Numéro masqué</dt><dd>{item.phoneMasked || '—'}</dd></div>
                            <div><dt>Formule</dt><dd>{item.planCode}</dd></div>
                            <div><dt>Traitement</dt><dd>{item.simulationMode === 'automatic'
                              ? 'Automatique (prestataire fictif)' : 'Manuel (test)'}</dd></div>
                            <div><dt>Créé le</dt><dd>{formatDate(item.createdAt)}</dd></div>
                            <div><dt>Résolu le</dt><dd>{formatDate(item.resolvedAt)}</dd></div>
                            <div><dt>Abonnement</dt><dd>{item.subscription?.id ?? 'Non créé'}</dd></div>
                            <div><dt>Début Premium</dt><dd>{formatDate(item.subscription?.startsAt)}</dd></div>
                            <div><dt>Fin Premium</dt><dd>{formatDate(item.subscription?.endsAt)}</dd></div>
                            <div><dt>Droits Premium enregistrés</dt><dd>
                              {item.subscription?.entitlementCreated ? 'Oui' : 'Non'}
                            </dd></div>
                            <div><dt>Accès associé actuellement actif</dt><dd>
                              {item.subscription?.entitlementActive ? 'Oui' : 'Non'}
                            </dd></div>
                          </dl>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bo-payment-history__pagination">
            <span>
              {summary.total} résultat(s) · Page {page} sur {Math.max(totalPages, 1)}
            </span>
            <div className="bo-actions">
              <button type="button" className="bo-button bo-button--ghost"
                disabled={page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>
                Précédent
              </button>
              <button type="button" className="bo-button bo-button--ghost"
                disabled={page >= totalPages}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>
                Suivant
              </button>
            </div>
          </div>
          <p className="bo-muted bo-payment-history__note">
            Une confirmation fictive valide la logique d’abonnement, pas un véritable
            encaissement. En production, la preuve devra venir du prestataire de paiement.
          </p>
        </>
      )}
    </section>
  );
}
